/**
 * RefeedTimer — 60min countdown after a training log is added.
 * Only relevant for Bloco 2/3 (Carb Cycling / Performance).
 *
 * Props:
 *   lastTrainingAt: ISO timestamp of the most recent training log
 *   phaseId: current phase id
 *   onOpenLog: callback to open nutrition log modal
 */

import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Zap, Clock } from "lucide-react";

const WINDOW_MIN = 60;

function _diffMin(iso) {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  const now = Date.now();
  return (now - then) / 60000;
}

export default function RefeedTimer({ lastTrainingAt, phaseId, onOpenLog }) {
  const [remaining, setRemaining] = useState(null);

  useEffect(() => {
    const tick = () => {
      const elapsed = _diffMin(lastTrainingAt);
      if (elapsed == null || elapsed > WINDOW_MIN + 5) {
        setRemaining(null);
        return;
      }
      setRemaining(Math.max(0, WINDOW_MIN - elapsed));
    };
    tick();
    const id = setInterval(tick, 30000);
    return () => clearInterval(id);
  }, [lastTrainingAt]);

  // Only show in Bloco 2/3 with active window
  if (![2, 3].includes(phaseId) || remaining == null || remaining <= 0) return null;

  const pctLeft = (remaining / WINDOW_MIN) * 100;
  const urgent = remaining < 20;
  const color = urgent ? "#ef4444" : "#f97316";

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className="mb-4 p-4 rounded-2xl border relative overflow-hidden"
      style={{ borderColor: `${color}50`, background: `${color}10` }}
      data-testid="refeed-timer"
    >
      <motion.div
        className="absolute inset-y-0 left-0 pointer-events-none"
        style={{ background: `${color}20`, width: `${pctLeft}%` }}
        animate={{ width: `${pctLeft}%` }}
        transition={{ duration: 0.6 }}
      />
      <div className="relative flex items-center gap-3">
        <motion.div
          animate={{ scale: [1, 1.15, 1] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ background: `${color}25`, boxShadow: `0 0 20px ${color}60` }}
        >
          <Zap className="w-5 h-5" style={{ color }} />
        </motion.div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-widest" style={{ color }}>
            Janela Anabólica {urgent ? "· URGENTE" : "· Refeed pós-treino"}
          </p>
          <p className="text-sm font-bold text-zinc-100 leading-tight">
            {Math.ceil(remaining)}min para maximizar mTOR
          </p>
          <p className="text-[11px] text-zinc-400 mt-0.5">
            Come agora: {phaseId === 2 ? "50-80g carbs" : "80-120g carbs"} + 30-40g proteína
          </p>
        </div>
        <button
          onClick={onOpenLog}
          className="text-[10px] uppercase tracking-wider px-3 py-1.5 rounded-full font-bold whitespace-nowrap"
          style={{ background: color, color: "#000" }}
          data-testid="refeed-timer-log"
        >
          <Clock className="w-3 h-3 inline mr-1" /> Registar
        </button>
      </div>
    </motion.div>
  );
}
